import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Loader2, Shield, DollarSign, Users } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const SuperAdminJSAManager = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activating, setActivating] = useState(false);
  const [freeAccess, setFreeAccess] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/companies/jsa-status');
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateJSA = async (isFree) => {
    if (!selectedCompany) return;

    setActivating(true);
    try {
      const response = await axiosInstance.post('/admin/jsa-module/activate', {
        companyId: selectedCompany.id,
        isFree: isFree
      });

      toast.success(response.data.message);
      setShowActivateDialog(false);
      setSelectedCompany(null);
      await loadCompanies();
    } catch (error) {
      console.error('Error activating JSA:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate JSA module');
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivateJSA = async (company) => {
    if (!window.confirm(`Deactivate JSA Module for ${company.name}?`)) return;

    try {
      const response = await axiosInstance.post('/admin/jsa-module/deactivate', {
        companyId: company.id
      });

      toast.success(response.data.message);
      await loadCompanies();
    } catch (error) {
      console.error('Error deactivating JSA:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate JSA module');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
            <p className="text-gray-500 mt-2">Loading companies...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                JSA Module Management
              </CardTitle>
              <CardDescription>Activate or deactivate JSA module for companies</CardDescription>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Companies: {companies.length}</p>
              <p className="text-sm text-green-600 font-semibold">
                Active JSA: {companies.filter(c => c.jsaModuleEnabled).length}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {companies.map((company) => (
              <div
                key={company.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{company.name}</h3>
                      {company.jsaModuleEnabled ? (
                        <Badge className="bg-green-600">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                      {company.jsaModuleFreeAccess && (
                        <Badge className="bg-blue-600">FREE</Badge>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span>Code: <strong>{company.companyCode}</strong></span>
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {company.employeeCount || 0} employees
                      </span>
                      <span>
                        Tier: <strong>{company.jsaModulePricing?.tier_name}</strong>
                      </span>
                      {company.jsaModuleEnabled && (
                        <span className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          ${company.jsaModulePrice?.toFixed(2) || '0.00'}/year
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {company.jsaModuleEnabled ? (
                      <Button
                        onClick={() => handleDeactivateJSA(company)}
                        variant="destructive"
                        size="sm"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedCompany(company);
                          setFreeAccess(false);
                          setShowActivateDialog(true);
                        }}
                        size="sm"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Activate
                      </Button>
                    )}
                  </div>
                </div>

                {company.jsaModuleEnabled && company.jsaModuleDaysRemaining !== undefined && (
                  <div className="mt-2 text-xs text-gray-500">
                    Expires in: {company.jsaModuleDaysRemaining} days
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activation Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Activate JSA Module</DialogTitle>
            <DialogDescription>
              Activate JSA module for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-semibold mb-2">Company Details:</p>
              <div className="space-y-1 text-sm text-blue-800">
                <p>Company: {selectedCompany?.name}</p>
                <p>Code: {selectedCompany?.companyCode}</p>
                <p>Employees: {selectedCompany?.employeeCount}</p>
                <p>Tier: {selectedCompany?.jsaModulePricing?.tier_name}</p>
                <p className="font-semibold">
                  Regular Price: ${selectedCompany?.jsaModulePricing?.annual_price?.toFixed(2)}/year
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={freeAccess}
                  onChange={(e) => setFreeAccess(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Activate as FREE (no charge)</span>
              </label>
            </div>

            {freeAccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  ✓ This company will receive FREE access to the JSA module
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  setShowActivateDialog(false);
                  setSelectedCompany(null);
                  setFreeAccess(false);
                }}
                variant="outline"
                className="flex-1"
                disabled={activating}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleActivateJSA(freeAccess)}
                className="flex-1"
                disabled={activating}
              >
                {activating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Activate JSA
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuperAdminJSAManager;
